import React from 'react';

import { Flex, Heading, Text } from '@rover/kibble/core';
import useTheme from '@rover/kibble/themes/useTheme';
import { syndeticInterleave } from '@rover/utilities/array';

// TODO (DEV-104195): Translate all text in file before training goes international

interface SearchResultGroupingType {
  title?: string | JSX.Element;
  subtitle?: string | JSX.Element;
  children: JSX.Element[] | JSX.Element;
}

const SearchResultGrouping = ({
  children = [],
  title,
  subtitle,
}: SearchResultGroupingType): JSX.Element => {
  const theme = useTheme();
  const isTitleString = typeof title === 'string';
  const isSubtitleString = typeof subtitle === 'string';

  return (
    <Flex
      background="primary"
      mt="10x"
      flexDirection="column"
      borderBottom="1px solid"
      borderColor="border.primary"
      data-testid="search-result-grouping"
      sx={{
        '&:last-child': {
          borderBottom: ['none', `1px solid ${theme.colors.border.primary}`],
        },
        '&:first-child': {
          mt: '0x',
        },
      }}
    >
      <Flex
        display={title || subtitle ? 'flex' : 'none'}
        py="6x"
        px="4x"
        flexDirection="column"
        borderBottom="1px solid"
        borderColor="border.primary"
        data-qa-id="search-result-grouping-title"
        data-testid="search-result-grouping-title"
        gap="2x"
        sx={{
          '&:last-child': {
            borderBottom: 'none',
          },
        }}
      >
        {!!title &&
          (isTitleString ? (
            <Heading size="300" mb="0x">
              {title}
            </Heading>
          ) : (
            <Flex>{title}</Flex>
          ))}
        {!!subtitle &&
          (isSubtitleString ? <Text size="100">{subtitle}</Text> : <Flex>{subtitle}</Flex>)}
      </Flex>
      {children}
    </Flex>
  );
};

const FULL_MATCH_TITLE = 'Your best matches';

type FullMatchSubtitleProps = {
  needs: string[];
};
const FullMatchSubtitle = ({ needs }: FullMatchSubtitleProps): JSX.Element => {
  const needsList = needs.map((need, i) => {
    return (
      <Text fontWeight="semibold" key={i} size="100">
        {need.toLowerCase()}
      </Text>
    );
  });

  const joinedNeedsList = syndeticInterleave(needsList);

  return <Text size="100">These trainers can help with {joinedNeedsList}.</Text>;
};

const PARTIAL_MATCH_TITLE = 'Find a match';

const PartialMatchSubtitle = (): JSX.Element => {
  return (
    <Text size="100">
      These trainers can help with your dog's needs.{' '}
      <Text fontWeight="semibold" size="100">
        View their profiles to learn more.
      </Text>
    </Text>
  );
};

const NO_MATCH_TITLE = 'Modify your search';

const NoMatchSubtitle: React.FC = () => {
  return (
    <Text size="100">
      There aren't any trainers in your area that meet these needs.{' '}
      <Text fontWeight="semibold" size="100">
        Try changing or removing some of your filters for more results.
      </Text>
    </Text>
  );
};

const OTHER_MATCH_TITLE = 'Other trainers for you';

const OTHER_MATCH_INCLUDING_PARTIAL_MATCHES_SUBTITLE =
  'These trainers did not indicate experience with all of your training needs.';

type GroupingVariants =
  | 'fullMatch'
  | 'partialMatch'
  | 'noMatch'
  | 'otherMatchIncludingPartialMatches'
  | 'otherMatch';

type GroupVariantProps = {
  children: JSX.Element[];
  variant: GroupingVariants;
  needs?: string[];
};

export const SearchResultGroupingVariant = ({
  children,
  variant,
  needs = [],
}: GroupVariantProps): JSX.Element | null => {
  if (children.length === 0) {
    return null;
  }

  const groupingVariantMapping = {
    fullMatch: { title: FULL_MATCH_TITLE, subtitle: <FullMatchSubtitle needs={needs} /> },
    partialMatch: { title: PARTIAL_MATCH_TITLE, subtitle: <PartialMatchSubtitle /> },
    noMatch: { title: NO_MATCH_TITLE, subtitle: <NoMatchSubtitle /> },
    otherMatchIncludingPartialMatches: {
      title: OTHER_MATCH_TITLE,
      subtitle: OTHER_MATCH_INCLUDING_PARTIAL_MATCHES_SUBTITLE,
    },
    otherMatch: {
      title: OTHER_MATCH_TITLE,
    },
  };
  return (
    <SearchResultGrouping {...groupingVariantMapping[variant]}>{children}</SearchResultGrouping>
  );
};

export default SearchResultGrouping;
